import type { ViewerShell } from '../../../shell';
import { isCompactChrome } from '../../../ui/compact';
import type { Viewport } from '../../../viewport';
import type { Box } from '../api';
import type { Point01 } from './api';

/**
 * Picks a point or drags a box on the active viewport for the Asistente tools. A transparent layer
 * covers the cell while picking, so the left-button tool (W/L, a measurement…) never sees the click
 * and nothing is drawn into the annotation store. Coordinates come back normalised (0–1) on the
 * image, through cornerstone's canvas → pixel transform (zoom, pan, rotation and flips honoured).
 * Esc or a right click cancels.
 */

export const PICKER_TEXT = {
  point: 'Haga clic sobre la lesión. Esc para cancelar.',
  box: 'Arrastre un recuadro sobre el área. Esc para cancelar.',
} as const;

/** A box narrower than this many image pixels is taken as a stray click. */
export const MIN_BOX_PX = 4;

export interface PickTarget {
  viewport: Viewport;
  columns: number;
  rows: number;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** The image under `event`, in image pixels. */
function imagePixel(vp: Viewport, event: PointerEvent): { x: number; y: number } {
  const canvas = vp.element.querySelector('canvas');
  const rect = (canvas ?? vp.element).getBoundingClientRect();
  const scaleX = canvas && rect.width ? canvas.width / rect.width : 1;
  const scaleY = canvas && rect.height ? canvas.height / rect.height : 1;
  const point = { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY };
  return window.cornerstone.canvasToPixel(vp.element, point);
}

/** Normalised box from two image-pixel corners (any drag direction). */
export function boxFromCorners(a: { x: number; y: number }, b: { x: number; y: number }, columns: number, rows: number): Box | null {
  if (Math.abs(a.x - b.x) < MIN_BOX_PX || Math.abs(a.y - b.y) < MIN_BOX_PX) return null;
  const x0 = clamp01(Math.min(a.x, b.x) / columns);
  const x1 = clamp01(Math.max(a.x, b.x) / columns);
  const y0 = clamp01(Math.min(a.y, b.y) / rows);
  const y1 = clamp01(Math.max(a.y, b.y) / rows);
  return x1 > x0 && y1 > y0 ? [x0, y0, x1, y1] : null;
}

/** The active viewport and its image size, when it shows an image. */
export function pickTarget(shell: ViewerShell): PickTarget | null {
  const vp = shell.activeViewport();
  const image = vp?.currentImage();
  if (!vp || !image || !image.columns || !image.rows) return null;
  return { viewport: vp, columns: image.columns, rows: image.rows };
}

interface Layer {
  node: HTMLDivElement;
  done: () => void;
}

function openLayer(target: PickTarget, hint: string, onCancel: () => void): Layer {
  const node = document.createElement('div');
  node.className = 'assistant-picker';
  node.setAttribute('role', 'application');
  node.setAttribute('aria-label', hint);
  const help = document.createElement('div');
  help.className = 'assistant-picker-hint';
  help.textContent = hint;
  node.appendChild(help);
  target.viewport.container.appendChild(node);
  const onKey = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  };
  document.addEventListener('keydown', onKey, true);
  node.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    onCancel();
  });
  return {
    node,
    done: () => {
      document.removeEventListener('keydown', onKey, true);
      node.remove();
    },
  };
}

let current: (() => void) | null = null;

/** On the compact chrome the drawer covers the image: close it while picking, reopen it after. */
function revealForPick(shell: ViewerShell): () => void {
  if (!isCompactChrome() || !shell.isRightDrawerOpen()) return () => undefined;
  shell.setRightDrawer(false);
  return () => shell.setRightDrawer(true);
}

/** Cancels a pick in progress (another tool started, the tab was hidden). */
export function cancelPick(): void {
  current?.();
}

/** One click on the active image; null when cancelled or when no image is shown. */
export function pickPoint(shell: ViewerShell): Promise<{ target: PickTarget; point: Point01 } | null> {
  cancelPick();
  const restore = revealForPick(shell);
  const target = pickTarget(shell);
  if (!target) {
    restore();
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const finish = (value: { target: PickTarget; point: Point01 } | null) => {
      current = null;
      layer.done();
      restore();
      resolve(value);
    };
    const layer = openLayer(target, PICKER_TEXT.point, () => finish(null));
    current = () => finish(null);
    layer.node.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      const p = imagePixel(target.viewport, event);
      finish({ target, point: { x: clamp01(p.x / target.columns), y: clamp01(p.y / target.rows) } });
    });
  });
}

/** One dragged box on the active image (drawn while dragging); null when cancelled or too small. */
export function pickBox(shell: ViewerShell): Promise<{ target: PickTarget; box: Box } | null> {
  cancelPick();
  const restore = revealForPick(shell);
  const target = pickTarget(shell);
  if (!target) {
    restore();
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const finish = (value: { target: PickTarget; box: Box } | null) => {
      current = null;
      layer.done();
      restore();
      resolve(value);
    };
    const layer = openLayer(target, PICKER_TEXT.box, () => finish(null));
    current = () => finish(null);
    const rubber = document.createElement('div');
    rubber.className = 'assistant-picker-box';
    rubber.hidden = true;
    layer.node.appendChild(rubber);
    let start: { x: number; y: number; cx: number; cy: number } | null = null;
    layer.node.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      layer.node.setPointerCapture?.(event.pointerId);
      const p = imagePixel(target.viewport, event);
      start = { ...p, cx: event.clientX, cy: event.clientY };
    });
    layer.node.addEventListener('pointermove', (event) => {
      if (!start) return;
      const rect = layer.node.getBoundingClientRect();
      rubber.hidden = false;
      rubber.style.left = `${Math.min(start.cx, event.clientX) - rect.left}px`;
      rubber.style.top = `${Math.min(start.cy, event.clientY) - rect.top}px`;
      rubber.style.width = `${Math.abs(event.clientX - start.cx)}px`;
      rubber.style.height = `${Math.abs(event.clientY - start.cy)}px`;
    });
    layer.node.addEventListener('pointerup', (event) => {
      if (!start) return;
      const box = boxFromCorners(start, imagePixel(target.viewport, event), target.columns, target.rows);
      finish(box ? { target, box } : null);
    });
  });
}
